import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { RestaurantProvider } from "@/lib/restaurantContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import RestaurantDashboard from "@/pages/RestaurantDashboard";
import RestaurantProfile from "@/pages/RestaurantProfile";
import MenuManagement from "@/pages/MenuManagement";
import QRGenerator from "@/pages/QRGenerator";
import CustomerMenu from "@/pages/CustomerMenu";
import UserDetail from "@/pages/UserDetail";
import RestaurantsList from "@/pages/RestaurantsList";
import Billing from "@/pages/Billing";
import MyProfile from "@/pages/MyProfile";

function AuthedRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
}

function RestaurantBoundary({ children }) {
  const { user } = useAuth();
  return <RestaurantProvider user={user}>{children}</RestaurantProvider>;
}

function App() {
  return (
    <AuthProvider>
      <RestaurantBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/m/:slug" element={<CustomerMenu />} />

            <Route path="/dashboard" element={<ProtectedRoute role="owner"><RestaurantDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/profile" element={<ProtectedRoute role="owner"><RestaurantProfile /></ProtectedRoute>} />
            <Route path="/dashboard/menu" element={<ProtectedRoute role="owner"><MenuManagement /></ProtectedRoute>} />
            <Route path="/dashboard/qr" element={<ProtectedRoute role="owner"><QRGenerator /></ProtectedRoute>} />
            <Route path="/dashboard/restaurants" element={<ProtectedRoute role="owner"><RestaurantsList /></ProtectedRoute>} />
            <Route path="/dashboard/billing" element={<ProtectedRoute role="owner"><Billing /></ProtectedRoute>} />
            <Route path="/dashboard/me" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users/:id" element={<ProtectedRoute role="super_admin"><UserDetail /></ProtectedRoute>} />
            <Route path="/admin/restaurants" element={<ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />

            <Route path="/app" element={<AuthedRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </RestaurantBoundary>
    </AuthProvider>
  );
}

export default App;
